/**
 * The closing screen of a Shivurt enquiry.
 *
 * Everything on it is chosen to answer the only questions a buyer has once they
 * have approved: did it go through, what exactly did I confirm, what happens
 * now, and what can I do next. So: one stamp, five facts, one next-step line,
 * two actions — no celebration graphics, and no restatement of the document they
 * just read.
 *
 * The headline and the next-step line are component-owned copy keyed off
 * `outcome`, not model prose: this is the one screen where a loose sentence
 * would become a promise Shivurt cannot keep. `recorded` says the request is
 * confirmed and theirs to send; `handed_off` says our sourcing desk has it.
 * Nothing here claims a supplier was contacted.
 */
import type { FC } from 'react';
import { ArrowRight, Check } from 'lucide-react';
import { defineMessages, useIntl } from 'react-intl';

import type { A2uiNodeViewProps } from '@/app/lib/a2ui/catalog.tsx';
import { useSurfaceAction } from '@/app/lib/a2ui/surface-context.ts';
import { arr, optStr, str } from '@/app/lib/a2ui/props.ts';
import { ArtifactCard, PrimaryButton, SecondaryButton } from './procurement-parts.tsx';

const messages = defineMessages({
  kindRecorded: {
    id: 'success.kind.recorded',
    defaultMessage: 'Confirmed',
    description: 'Eyebrow label on the closing screen once a request is confirmed.',
  },
  kindHandedOff: {
    id: 'success.kind.handedOff',
    defaultMessage: 'Handed to sourcing',
    description: 'Eyebrow label on the closing screen when a person is taking the request forward.',
  },
  headlineRecorded: {
    id: 'success.headline.recorded',
    defaultMessage: 'Your procurement request is confirmed',
    description: 'Main headline of the closing screen after the buyer approved the request.',
  },
  headlineHandedOff: {
    id: 'success.headline.handedOff',
    defaultMessage: 'Your request is with our sourcing team',
    description: 'Main headline of the closing screen when the request went to human sourcing.',
  },
  product: {
    id: 'success.summary.product',
    defaultMessage: 'Product',
    description: 'Summary row label for what is being bought.',
  },
  quantity: {
    id: 'success.summary.quantity',
    defaultMessage: 'Quantity',
    description: 'Summary row label for the confirmed quantity.',
  },
  supplier: {
    id: 'success.summary.supplier',
    defaultMessage: 'Supplier',
    description: 'Summary row label when the request is addressed to one supplier.',
  },
  suppliers: {
    id: 'success.summary.suppliers',
    defaultMessage: 'Suppliers',
    description: 'Summary row label when the request is addressed to several suppliers.',
  },
  reference: {
    id: 'success.summary.reference',
    defaultMessage: 'Reference number',
    description: 'Summary row label for the request reference.',
  },
  respondBy: {
    id: 'success.summary.respondBy',
    defaultMessage: 'Response requested by',
    description: 'Summary row label for the date suppliers are asked to reply by.',
  },
  nextStep: {
    id: 'success.nextStep',
    defaultMessage: 'What happens next',
    description: 'Heading of the next-step line on the closing screen.',
  },
  timelineRecordedBy: {
    id: 'success.timeline.recordedBy',
    defaultMessage:
      'Send the request to your supplier and ask them to reply by {date}. I cannot deliver it to them for you yet, so the document is yours to send.',
    description: 'Next step when the request is confirmed and a response date was agreed.',
  },
  timelineRecorded: {
    id: 'success.timeline.recorded',
    defaultMessage:
      'Send the request to your supplier and set the reply date you want. I cannot deliver it to them for you yet, so the document is yours to send.',
    description: 'Next step when the request is confirmed and no response date was set.',
  },
  timelineHandedOffBy: {
    id: 'success.timeline.handedOffBy',
    defaultMessage:
      'Our sourcing team picks this up from here and works to your {date} response date. Quote the reference above in any message about it.',
    description: 'Next step for a handoff where a response date was agreed.',
  },
  timelineHandedOff: {
    id: 'success.timeline.handedOff',
    defaultMessage:
      'Our sourcing team picks this up from here. Quote the reference above in any message about it.',
    description: 'Next step for a handoff with no response date.',
  },
  startNew: {
    id: 'success.action.startNew',
    defaultMessage: 'Start a new procurement request',
    description: 'Primary action that begins a fresh sourcing enquiry.',
  },
  viewDetails: {
    id: 'success.action.viewDetails',
    defaultMessage: 'View request details',
    description: 'Secondary action that reopens the recorded request document.',
  },
  stampLabel: {
    id: 'success.stamp.label',
    defaultMessage: 'Confirmed',
    description: 'Accessible name of the checkmark stamp on the closing screen.',
  },
});

interface SuccessSupplier {
  name: string;
  tierLabel?: string;
}

function readSuppliers(value: unknown): SuccessSupplier[] {
  return arr(value)
    .filter(
      (entry): entry is Record<string, unknown> =>
        typeof entry === 'object' && entry !== null && !Array.isArray(entry),
    )
    .map((entry) => {
      const tierLabel = optStr(entry.tierLabel);
      return { name: str(entry.name), ...(tierLabel ? { tierLabel } : {}) };
    })
    .filter((entry) => entry.name.length > 0);
}

export const ProcurementSuccess: FC<A2uiNodeViewProps> = ({ node }) => {
  const intl = useIntl();
  const dispatch = useSurfaceAction();
  const handedOff = str(node.props.outcome) === 'handed_off';
  const reference = str(node.props.reference);
  const product = str(node.props.product);
  const quantity = optStr(node.props.quantity);
  const respondBy = optStr(node.props.respondBy);
  const nextStepNote = optStr(node.props.nextStepNote);
  const suppliers = readSuppliers(node.props.suppliers);

  const timeline = handedOff
    ? respondBy
      ? intl.formatMessage(messages.timelineHandedOffBy, { date: respondBy })
      : intl.formatMessage(messages.timelineHandedOff)
    : respondBy
      ? intl.formatMessage(messages.timelineRecordedBy, { date: respondBy })
      : intl.formatMessage(messages.timelineRecorded);

  const rows: { label: string; value: string; caption?: string }[] = [
    { label: intl.formatMessage(messages.product), value: product },
    ...(quantity ? [{ label: intl.formatMessage(messages.quantity), value: quantity }] : []),
    ...(suppliers.length > 0
      ? [
          {
            label: intl.formatMessage(
              suppliers.length === 1 ? messages.supplier : messages.suppliers,
            ),
            value: suppliers.map((entry) => entry.name).join(', '),
            // One supplier needs only its tier under the name; several need the
            // name repeated so each label is attached to the right business.
            ...(suppliers.some((entry) => entry.tierLabel)
              ? {
                  caption:
                    suppliers.length === 1
                      ? (suppliers[0]?.tierLabel ?? '')
                      : suppliers
                          .filter((entry) => entry.tierLabel)
                          .map((entry) => `${entry.name} — ${entry.tierLabel ?? ''}`)
                          .join(' · '),
                }
              : {}),
          },
        ]
      : []),
    { label: intl.formatMessage(messages.reference), value: reference },
    ...(respondBy ? [{ label: intl.formatMessage(messages.respondBy), value: respondBy }] : []),
  ].filter((row) => row.value.length > 0);

  return (
    <ArtifactCard>
      <header className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:gap-5">
        {/* The stamp: a quiet mark of record, sized like a seal rather than a
            celebration. Its meaning is in the heading beside it, so the ring is
            decorative and the label is for screen readers only. */}
        <span
          role="img"
          aria-label={intl.formatMessage(messages.stampLabel)}
          className="grid h-12 w-12 flex-none place-items-center rounded-full border-2 border-success/40 bg-success/12 text-success"
        >
          <Check aria-hidden="true" className="h-6 w-6" strokeWidth={3} />
        </span>
        <div className="min-w-0">
          <p className="text-body-xs font-semibold uppercase tracking-caps text-brand-accent-ink">
            {intl.formatMessage(handedOff ? messages.kindHandedOff : messages.kindRecorded)}
          </p>
          <h1 className="mt-1 text-balance font-display text-heading-lg font-bold tracking-tight text-foreground">
            {intl.formatMessage(handedOff ? messages.headlineHandedOff : messages.headlineRecorded)}
          </h1>
        </div>
      </header>

      <dl className="flex flex-col divide-y divide-border rounded-lg border border-border bg-muted/50 px-4">
        {rows.map((row) => (
          <div
            key={row.label}
            className="grid gap-1 py-3 sm:grid-cols-[minmax(0,12rem)_1fr] sm:gap-4"
          >
            <dt className="text-body-sm font-semibold text-muted-foreground">{row.label}</dt>
            <dd className="min-w-0">
              <p className="break-words text-body-base text-foreground" translate="no">
                {row.value}
              </p>
              {row.caption ? (
                <p className="mt-0.5 text-body-xs text-muted-foreground-subtle">{row.caption}</p>
              ) : null}
            </dd>
          </div>
        ))}
      </dl>

      <section className="flex items-start gap-3">
        <ArrowRight aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-brand-accent" />
        <div className="min-w-0">
          <h2 className="text-body-xs font-semibold uppercase tracking-caps text-muted-foreground">
            {intl.formatMessage(messages.nextStep)}
          </h2>
          <p className="mt-1 max-w-[68ch] text-body-base text-foreground">{timeline}</p>
          {nextStepNote ? (
            <p className="mt-1 max-w-[68ch] text-body-sm text-muted-foreground">{nextStepNote}</p>
          ) : null}
        </div>
      </section>

      <div className="flex flex-col gap-3 border-t border-border pt-5 sm:flex-row sm:items-center">
        <PrimaryButton
          label={intl.formatMessage(messages.startNew)}
          onClick={() => dispatch?.('startNewRequest', {})}
        />
        <span className="inline-flex">
          <SecondaryButton
            label={intl.formatMessage(messages.viewDetails)}
            onClick={() => dispatch?.('viewRequestDetails', { reference })}
          />
        </span>
      </div>
    </ArtifactCard>
  );
};
